import React, { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Btn } from '../../../ForBrands/ui/BrandUI';
import { usePhoneTheme } from '../../../ForBrands/useIsPhone';
import { HubInput, SecondaryBtn } from '../../../ForAthletes/v2/edit/editHubPieces';
import { parseEmails, shareProblem } from './coachProfileModel';
import { SUPPORT_EMAIL, TeamCodeBox, VerifiedBadge } from './coachProfilePieces';
import { codeCoverageLine } from '../teamCodes';

// The sidebar card of the profile hub (Figma 01 pending, 01a verified). It
// takes the place the athlete hub gives its profile strength. On phones it
// heads the hub list (2471:4479 pending, 2471:4547 verified): the same card,
// with the send form stacked (a 56px field from the hub's .bm-phone-form,
// the hint, then a full-width 52px Send).

const CardShell = ({ badge, title, children }) => (
  <div className="flex flex-col gap-[10px] rounded-[16px] border border-bm-mutedbg bg-white px-[18px] pb-[18px] pt-4">
    <div className="flex items-center justify-between gap-3">
      <span className="bm-overline">Verification</span>
      {badge}
    </div>
    <h2 className="bm-h-s text-bm-ink">{title}</h2>
    {children}
  </div>
);

const Body = ({ children }) => <p className="bm-body-m text-bm-muted">{children}</p>;
const Note = ({ children }) => <p className="bm-body-s text-bm-tertiary">{children}</p>;

const SendCodeForm = ({ disabled, onSend }) => {
  const phone = usePhoneTheme();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const parsed = parseEmails(text);
    const issue = shareProblem(parsed);
    if (issue) {
      setProblem(issue);
      return;
    }
    setBusy(true);
    setProblem('');
    try {
      const { sent = [] } = (await onSend(parsed.valid)) || {};
      toast.success(`Code sent to ${sent.length} address${sent.length === 1 ? '' : 'es'}.`);
      setText('');
    } catch (err) {
      setProblem(err.message || 'Could not send your code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // `flex-1` sizes the field beside Send in the desktop row. In the phone
  // column it would size the height instead and squash the 56px field, so
  // there the field keeps its own height and the full width. Phones also
  // leave out type="text" (the default): the forms plugin's [type=text] rule
  // would paint a grey-500 border over the V2 field border.
  const input = (
    <HubInput
      type={phone ? undefined : 'text'}
      aria-label="Email addresses"
      placeholder="Email addresses"
      value={text}
      disabled={disabled || busy}
      onChange={(e) => {
        setText(e.target.value);
        if (problem) setProblem('');
      }}
      className={phone ? 'min-w-0' : 'min-w-0 flex-1'}
    />
  );
  const sendLabel = busy ? 'Sending…' : 'Send';
  const sendOff = disabled || busy || !text.trim();
  const status = problem ? (
    <p role="alert" className="bm-body-s text-bm-critical">
      {problem}
    </p>
  ) : (
    <Note>Separate multiple addresses with commas. We'll email a code to each one.</Note>
  );

  if (phone) {
    return (
      <form onSubmit={submit} className="flex flex-col gap-[7px] pt-[6px]">
        <span className="bm-overline">Send your code by email</span>
        {input}
        {status}
        <Btn type="submit" variant="secondary" size="lg" className="mt-[3px] w-full" disabled={sendOff}>
          {sendLabel}
        </Btn>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-[7px] pt-[6px]">
      <span className="bm-overline">Send your code by email</span>
      <div className="flex items-center gap-2">
        {input}
        <SecondaryBtn type="submit" disabled={sendOff}>
          {sendLabel}
        </SecondaryBtn>
      </div>
      {status}
    </form>
  );
};

// Phones size it as every phone button (52px, full width).
const FinishOnboarding = () => {
  const phone = usePhoneTheme();
  return phone ? (
    <Btn variant="secondary" size="lg" className="w-full" to="/onboarding">
      Finish onboarding
    </Btn>
  ) : (
    <Btn variant="secondary" to="/onboarding">
      Finish onboarding
    </Btn>
  );
};

// The code of the coach's primary team: one code per team, shared by every
// coach of the team (dev call, 2026-10-07). Additional teams show their own
// codes in Additional teams. A program code (10/19 notes, D14) covers every
// age group of the program: the line under it names the program and its
// age groups, from the primary team row of coach/profile/ (`team`:
// codeScope, programName, codeAgeGroups) or the verification record.
const coverageOf = (verification, team, teamName) => {
  const source = team && typeof team === 'object' ? team : verification || {};
  const program = (source.codeScope || source.scope) === 'program';
  return {
    teamName: (team && (team.displayName || team.name)) || teamName,
    programName: program ? source.programName || '' : '',
    ageGroups: program && Array.isArray(source.codeAgeGroups) ? source.codeAgeGroups : [],
  };
};

const VerifiedCard = ({ code, coverage, onSend }) => {
  const line = code ? codeCoverageLine(coverage, 'Team code for') : '';
  return (
    <CardShell badge={<VerifiedBadge />} title="You're verified">
      <Body>Athletes you coach can enter the code below during onboarding, or any time from their profile, to link to your team.</Body>
      <TeamCodeBox code={code} />
      {line && <Note>{line}</Note>}
      <SendCodeForm disabled={!code} onSend={onSend} />
    </CardShell>
  );
};

const VerificationCard = ({ verification, teamName = '', team = null, onSend }) => {
  if (!verification) return null;
  const { status, code } = verification;
  if (status === 'verified') return <VerifiedCard code={code} coverage={coverageOf(verification, team, teamName)} onSend={onSend} />;
  if (status === 'rejected') {
    return (
      <CardShell badge={<Badge tone="critical">Rejected</Badge>} title="Verification rejected">
        <Body>
          Your coach verification request was rejected. Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-bm-orange underline underline-offset-2">
            {SUPPORT_EMAIL}
          </a>{' '}
          to discuss next steps.
        </Body>
      </CardShell>
    );
  }
  if (status === 'not_submitted') {
    return (
      <CardShell badge={<Badge tone="passive">Not started</Badge>} title="Finish onboarding">
        <Body>Pick your team so an admin can verify your role. Your team code appears here once you are verified.</Body>
        <div>
          <FinishOnboarding />
        </div>
      </CardShell>
    );
  }
  return (
    <CardShell badge={<Badge tone="attention">Pending review</Badge>} title="Awaiting admin verification">
      <Body>
        We received your team selection. An admin reviews and verifies your role within 1 business day. Once approved, a team code appears here for
        your athletes to enter, which links them to your roster.
      </Body>
      <Note>CacheShare™ and the team pages stay locked until verification completes. You can edit your profile and check back any time.</Note>
    </CardShell>
  );
};

export default VerificationCard;
